import { useState, useCallback, useEffect } from "react";
import { useApp } from "../lib/store";
import { api } from "../lib/api";
import { Languages, Loader2 } from "lucide-react";

/**
 * On-demand AI translation of free-text (AI/user generated) content.
 * `texts` = array of original strings. `children(displayTexts, button)` renders
 * using the (possibly translated) array; `button` is the small toggle control.
 */
export function TranslateBlock({ texts, testid = "translate-block", children }) {
  const { t, lang } = useApp();
  const [translated, setTranslated] = useState(null); // array | null
  const [showOriginal, setShowOriginal] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setTranslated(null); setShowOriginal(false); }, [lang]);

  const run = useCallback(async () => {
    if (translated) { setShowOriginal((v) => !v); return; }
    setBusy(true);
    try {
      const r = await api.post("/translate", { texts, target_lang: lang });
      setTranslated(r.data.translations || texts);
      setShowOriginal(false);
    } catch {
      setTranslated(texts);
    } finally {
      setBusy(false);
    }
  }, [translated, texts, lang]);

  const display = translated && !showOriginal ? translated : texts;

  const button = (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid={`${testid}-btn`}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1B2A4A]/70 hover:text-[#1B2A4A] transition-colors disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
      {busy ? t("translating") : translated ? (showOriginal ? t("translate") : t("show_original")) : t("translate")}
    </button>
  );

  return children(display, button, { isTranslated: !!translated && !showOriginal });
}
